import { isJsonObject, type JsonObject, type JsonValue } from "@/components/guardians/jsonValue";
import type { Artifact, ThreadMessage } from "./types";

export interface ThreadArtifactCardContent {
  artifact_id?: string | number;
  artifact?: Artifact;
}

/** Narrow thread message content to a JSON object when possible. */
export function threadMessageJsonContent(
  content: ThreadMessage["content"],
): JsonObject | null {
  if (typeof content !== "object" || content === null || Array.isArray(content)) {
    return null;
  }
  return content as JsonObject;
}

function embeddedArtifact(value: JsonValue | undefined): Artifact | undefined {
  if (!isJsonObject(value)) return undefined;
  if (
    typeof value._id !== "string" ||
    typeof value.client_id !== "string" ||
    typeof value.kind !== "string"
  ) {
    return undefined;
  }
  return JSON.parse(JSON.stringify(value)) as Artifact;
}

/** Read ``artifact_id`` from a thread message content blob. */
export function readArtifactId(content: JsonObject): string | null {
  const ref = content.artifact_id;
  if (typeof ref === "string" && ref.length > 0) return ref;
  if (typeof ref === "number" && Number.isFinite(ref)) return String(ref);
  return null;
}

/** Parse ``artifact_card`` fields from a thread message content blob. */
export function parseThreadArtifactCardContent(
  content: JsonObject,
): ThreadArtifactCardContent {
  const artifactId = readArtifactId(content);
  return {
    artifact_id: artifactId ?? undefined,
    artifact: embeddedArtifact(content.artifact),
  };
}

/** True when ``artifact_card`` content still needs a fetch to render. */
export function threadArtifactCardNeedsFetch(content: JsonObject): boolean {
  if (parseThreadArtifactCardContent(content).artifact) return false;
  return readArtifactId(content) !== null;
}
